/* eslint-disable no-unused-expressions */
/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { useSelector } from "react-redux";
import CustomModal from "../../Core/Components/Modal/CustomModal";
import CloseIcon from "../../Common/Svgs/CloseIcon";
import ZoomButton, {
  RelocateButton,
} from "../../Core/Components/MapMarkerButtons";
import getCenterOfPolygon from "../../Utils/getCenterOfPolygon";
import { TRIP_STATUS } from "./Data/TripConstants";
import CompletedTripMapComponents from "./TripDetails/components/CompletedTripMapComponents";
import OngoingTripMapComponents from "./TripDetails/components/OngoingTripMapComponents";
import UpcomingTripMapComponents from "./TripDetails/components/UpcomingTripMapComponents";
import StopCoordinates from "./TripDetails/components/StopCoordinates";
import { generateMockDeviation } from "../../Utils/getMapData";
import { SnapToRoadModal } from "./CreateTrip/OptimisingRouteModal";
import { client } from "../../Utils/axiosClient";

const FullScreenMapForTrips = ({
  visible,
  onCancel,
  // polygonCoordinates,
  // showRoute = true,
  loading = false,
}) => {
  const [actualRoute, setActualRoute] = useState([]);
  const [stationaryPoints, setStationaryPoints] = useState([]);

  const { snapToRoadCoordinates, currentTrip, overallSummaryForTrip } =
    useSelector((state) => state.trips);

  const isUpcoming = currentTrip.statusId === TRIP_STATUS.UPCOMING;

  const isCompleted = currentTrip.statusId === TRIP_STATUS.COMPLETED;

  const isOngoing = currentTrip.statusId === TRIP_STATUS.ONGOING;

  const [showMap, setShowMap] = React.useState(false);
  const mapRef = React.useRef(null);

  const fastestRouteCoordinates =
    currentTrip?.fastestRouteCoordinates ||
    currentTrip?.fastest_route_coordinates ||
    [];

  const tempCoordinates = [-0.6803741, 34.7569482];

  React.useEffect(() => {
    setTimeout(() => {
      setShowMap(true);
    }, 100);
  }, []);

  const snapToRoadFarmCoordinates = snapToRoadCoordinates.map((snap) => {
    return [snap[1], snap[0]];
  });

  const { globalLocations } = useSelector((state) => state.locations);

  const stops = currentTrip?.stops || [];
  const stopsId = currentTrip?.stopsId || [];

  const isRoundTrip =
    stopsId.length > 1 && stopsId[0] === stopsId[stopsId.length - 1];

  const effectiveStopsId = isRoundTrip ? stopsId.slice(0, -1) : stopsId;

  const localCurrentTrip = effectiveStopsId
    .map((stopId) => stops.find((stop) => stop.stopId === stopId))
    .filter(Boolean);

  const stopCoordinates = localCurrentTrip?.map((stop) => {
    const currentLocation = globalLocations?.find(
      (location) =>
        location.name === stop.location ||
        location.name === stop.name ||
        location.name === stop.locationName,
    );

    if (currentLocation) {
      return [+currentLocation.lat, +currentLocation.long];
    }

    return [0, 0];
  });

  const deviation = useMemo(
    () => generateMockDeviation(stopCoordinates, stopCoordinates),
    [stopCoordinates],
  );

  const getDistanceInMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const toRad = (deg) => (deg * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const filteredStationaryPoints = stationaryPoints.filter((stationary) => {
    // Filter out if duration is less than 10 minutes
    if (stationary.duration_seconds < 600) {
      return false;
    }

    // Filter out if within 100m of any stop
    const isNearStop = stopCoordinates.some(([lat, lng]) => {
      return (
        getDistanceInMeters(lat, lng, stationary.lat, stationary.long) < 100
      );
    });

    return !isNearStop;
  });

  useEffect(() => {
    const fetchTrikeRouteAndStationaryPoints = async () => {
      const res = await client.get(
        `${process.env.REACT_APP_API_URL}/trike/get-trike-route-and-stationary-points`,
        {
          params: {
            tripId: currentTrip.id,
          },
        },
      );

      return res;
    };

    (async () => {
      const res = await fetchTrikeRouteAndStationaryPoints();
      console.log({ res });
      if (res?.data?.route?.length) {
        setActualRoute(
          res?.data?.route
            .filter((coord) => coord?.lat && coord?.long)
            .map((coord) => ({ lat: coord?.lat, lng: coord?.long })),
        );
        setStationaryPoints(res?.data?.stationary_points || []);
      }
    })();
  }, [currentTrip.id]);

  return (
    <CustomModal visible={visible} onCancel={onCancel} width="1296px" centered>
      <div className="relative w-full h-[700px] overflow-hidden rounded-md">
        {showMap && (
          <div className="w-full h-full">
            <div className="absolute z-[100] top-6 right-6">
              <div className="flex items-center justify-center w-8 h-8 rounded-md cursor-pointer abosolute bg-primary">
                <CloseIcon
                  className="w-4 h-4 stroke-white"
                  onClick={onCancel}
                />
              </div>
            </div>
            <MapContainer
              center={
                snapToRoadFarmCoordinates.length > 1
                  ? getCenterOfPolygon(snapToRoadFarmCoordinates)
                  : tempCoordinates
              }
              zoom={12}
              className="z-10 w-full h-full"
              zoomControl={false}
              ref={mapRef}
            >
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {loading ? (
                <div>
                  <SnapToRoadModal visible={loading} />
                </div>
              ) : (
                <>
                  {isCompleted && (
                    <CompletedTripMapComponents
                      deviation={deviation}
                      stopCoordinates={stopCoordinates}
                      actualRoute={actualRoute}
                      stationaryPoints={filteredStationaryPoints}
                      fastestRouteCoordinates={fastestRouteCoordinates}
                    />
                  )}
                  {isOngoing && (
                    <OngoingTripMapComponents
                      stopCoordinates={stopCoordinates}
                      // deviation={deviation}
                      actualStopCoordinates={stopCoordinates}
                      actualRoute={actualRoute}
                      stationaryPoints={filteredStationaryPoints}
                      fastestRouteCoordinates={fastestRouteCoordinates}
                    />
                  )}

                  {isUpcoming && (
                    <UpcomingTripMapComponents
                      stopCoordinates={stopCoordinates}
                      fastestRouteCoordinates={fastestRouteCoordinates}
                    />
                  )}
                  {/* stop markers */}
                  <StopCoordinates
                    stopCoordinates={stopCoordinates}
                    overallSummaryForTrip={overallSummaryForTrip}
                  />
                </>
              )}
            </MapContainer>
            <div className="absolute bottom-6 right-6 z-[100]">
              <div className="flex flex-col gap-y-3">
                <RelocateButton />
                <ZoomButton mapRef={mapRef} />
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default FullScreenMapForTrips;
